import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useMeContext } from "../../MeApp";
import { useMeFeatureFlags } from "../../hooks/useMeFeatureFlags";
import { StepHeading } from "@codel/dova-ds";
import { CouplePair } from "../components/CouplePair";
import { FirstSessionModule } from "../components/FirstSessionModule";
import { Button } from "../components/Button";
import { buildShareLinkUrl } from "../../auth/meToken";
import { shareInviteLink } from "../../auth/shareInvite";

/** Module card — single hairline border, centered content. Mirrors the
 *  welcome screen's session card. */
const CARD =
  "rounded-[var(--radius-md)] border border-[var(--border)] p-6 flex flex-col items-center text-center";

export function S11HomebaseWaiting() {
  const { couple, partner } = useMeContext();
  const { flags, isLoading: flagsLoading } = useMeFeatureFlags();
  const [copied, setCopied] = useState(false);

  // Homebase handoff (contracts §4.3): once the couple is enrolled, S11 stops
  // being a terminus and hands off to the hub. While the flag read is in
  // flight we render the existing stub below (dark default — no hub flash);
  // flag OFF leaves this component byte-identical to the pre-Homebase world.
  // S9's advance mechanics (markCompleted → onNext → step "homebase") are
  // untouched — the redirect lives here, on arrival.
  if (!flagsLoading && flags.homebase) {
    return <Navigate to="/me/home" replace />;
  }

  if (!couple || !partner) return null;

  const other = couple.partners.find((p) => p.id !== partner.id);
  const otherDone = !!other?.onboardingState.completedAt;
  const otherName = other?.firstName ?? "your partner";

  // The SAME couple-anchored shared link the opener arrived on — the second
  // partner opens it and self-selects at S2. (Couple-anchored + re-openable by
  // design; see `verify_couple_link_token`.)
  const inviteUrl = buildShareLinkUrl();

  const invite = async () => {
    if (!inviteUrl) return;
    const result = await shareInviteLink(inviteUrl);
    if (result === "copied") {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    // No StepShell here (S11 is its own shell), so no footer eats the home
    // indicator inset — pad the bottom by the safe area on top of the base
    // 2.5rem (py-12) so the last card clears iOS's home indicator.
    // `md:flex-1 md:min-h-0 md:overflow-y-auto` — S11 SCROLLS ITSELF above md.
    //
    // Every other step sits in StepShell, which owns a scroller; S11 is its own
    // shell and had none. That was survivable while the card could grow (it was
    // `min-h-[680px] max-h-[92vh]`), and stopped being so when the card became one
    // fixed height for the whole flow: the card is `md:overflow-hidden`, so content
    // past 680px is not merely off-screen, it is unreachable — no scrollbar, no
    // wheel, no touch. Measured here at 647px of content in 680, i.e. 33px of
    // headroom, which a wrapped therapist name or a longer locale date eats.
    //
    // md-only because below md the CARD is already `overflow-y-auto` as the
    // fallback for exactly this case, and two nested scrollers on a phone is worse
    // than the problem.
    <div className="px-6 pt-12 pb-[calc(2.5rem+env(safe-area-inset-bottom))] md:px-8 md:py-12 flex flex-col gap-8 md:flex-1 md:min-h-0 md:overflow-y-auto">
      <div className="dova-rise">
        <StepHeading align="center" title={`Welcome, ${partner.firstName}.`}>
          <p className="max-w-[34ch]">
            This is your home for Dova going forward. Bookmark this page to come
            back anytime.
          </p>
        </StepHeading>
      </div>

      <div className="flex flex-col gap-4">
        {/* Invite nudge — get the second partner onboarded before the session. */}
        {other && !otherDone && (
          <section
            className={`dova-rise ${CARD} gap-4`}
            style={{ animationDelay: "60ms" }}
          >
            {/* READER FIRST: the second disc is the one that can be an empty
                seat, so the order here is what makes the seat theirs. */}
            <CouplePair
              names={[partner.firstName, otherName]}
              otherJoined={false}
            />
            <div className="flex flex-col gap-2">
              <h2 className="font-serif text-xl font-medium tracking-[-0.01em] text-[var(--text-primary)]">
                Dova works better together
              </h2>
              <p className="max-w-[34ch] text-base leading-[1.5] text-[var(--text-secondary)]">
                Invite {otherName} so Dova can offer personalized guidance to
                both of you.
              </p>
            </div>
            {inviteUrl && (
              <Button onClick={() => void invite()}>
                {copied ? "Invite link copied" : `Invite ${otherName}`}
              </Button>
            )}
          </section>
        )}

        {/* Both partners done — a calm confirmation, no action needed. */}
        {other && otherDone && (
          <section
            className={`dova-rise ${CARD} gap-4`}
            style={{ animationDelay: "60ms" }}
          >
            {/* READER FIRST: the second disc is the one that can be an empty
                seat, so the order here is what makes the seat theirs. */}
            <CouplePair
              names={[partner.firstName, otherName]}
              otherJoined
            />
            <div className="flex flex-col gap-2">
              <h2 className="font-serif text-xl font-medium tracking-[-0.01em] text-[var(--text-primary)]">
                You’re both in
              </h2>
              <p className="max-w-[34ch] text-base leading-[1.5] text-[var(--text-secondary)]">
                You and {otherName} have both set up Dova. Look for your group
                chat with Dova in Messages.
              </p>
            </div>
          </section>
        )}

        {/* First session — the same SessionCard as the welcome screen. The
            reminder note sits just below the card, not inside it.

            The whole group is guarded on the session, note included: "A text
            reminder arrives before it starts" has no antecedent when nothing is
            booked, and it would be left explaining a card that is not there. */}
        {couple.firstSessionAt && (
          <div
            className="dova-rise flex flex-col gap-2"
            style={{ animationDelay: "120ms" }}
          >
            <FirstSessionModule
              firstSessionAt={couple.firstSessionAt}
              therapistName={couple.therapistName}
              durationMinutes={couple.firstSessionDurationMinutes}
              location={couple.firstSessionLocation}
              locationAddress={couple.firstSessionLocationAddress}
            />
            <p className="text-center text-xs leading-[1.5] text-[var(--text-tertiary)]">
              A text reminder arrives before it starts.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
